import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  useTheme,
  Alert,
  MenuItem,
  Chip,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import BoltIcon from '@mui/icons-material/Bolt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import executeSystemActionMutation from '../../../Apollo/mutations/executeSystemAction';
import getSystemActionTemplate from '../../../Apollo/queries/getSystemActionTemplate';
import getAdvisorSystems from '../../../Apollo/queries/getAdvisorSystems';
import { htmlToPlainText, plainTextToHtml } from '../../../utils/htmlPlainTextConverter';
import sanitizeEmailHtml from '../../../utils/sanitizeHtml';
import { getTomorrowDateBerlin } from '../../../features/system-accesses/utils/format';

type ActionType = 'apply';

interface AdvisorSystem {
  provider_id: number;
  provider_name: string;
  recipient_email: string;
  access_status: string;
  applied_at: string | null;
  cancelled_at: string | null;
  changed_at: string | null;
  scheduled_at: string | null;
  letter_template_name: string | null;
}

interface AdvisorValues {
  agent_id: number;
  name: string;
  surname: string;
  address: string;
  zip: string;
  city: string;
  country_id: number;
  branch_office_id: number;
  country?: { id: number; title: string } | null;
  branch_office?: { id: number; title: string } | null;
  email_corp: string;
  email_private: string;
  business_owner: string;
  phone?: string | null;
  mobile?: string | null;
  fax?: string | null;
  birthday?: string | null;
  is_male?: number | null;
  status_id?: number | null;
  rank_id?: number | null;
  level_id?: number | null;
  contract_type_id?: number | null;
  address2?: string | null;
  zip2?: string | null;
  city2?: string | null;
  country2_id?: number | null;
  iban?: string | null;
  bic?: string | null;
  bank_name?: string | null;
  tax_nr?: string | null;
  start_date?: string | null;
  agent_account_nr?: number | null;
  myjack_login?: string | null;
  mandateReference?: string | null;
}

interface SystemActionDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  advisorId: number;
  advisorValues: AdvisorValues;
  countries: { id: number; title: string }[];
  branchOffices: { id: number; title: string }[];
  // True while the agent form has unsaved edits. The body placeholders are
  // filled from the live form values (RITA-1281), but the subject is derived
  // backend-side from the PERSISTED advisor record (getSystemActionTemplate /
  // executeSystemAction → buildEmailSubject). While the form is dirty those two
  // snapshots can describe different advisors, so the dialog must not let the
  // user send a mail whose subject and body disagree — block until saved.
  formDirty: boolean;
  statuses?: { id: number; title: string }[];
  ranks?: { id: number; title: string }[];
  levels?: { id: number; title: string }[];
  contractTypes?: { id: number; title: string }[];
}

const APPLY_STYLE = {
  color: '#4ADE80',
  bgAlpha: '#4ADE8018',
  borderAlpha: '#4ADE8035',
  label: 'Beantragen',
};

const ACTION_TYPE: ActionType = 'apply';

function formatBirthday(birthday: string | null | undefined): string {
  if (!birthday) return '';
  const match = birthday.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return birthday;
}

// CONTRACT: is_male=1 → 'Männlich', is_male=0 → 'Weiblich', null/undefined → '' (AK-2/AK-4)
function formatGender(isMale: number | null | undefined): string {
  if (isMale === 1) return 'Männlich';
  if (isMale === 0) return 'Weiblich';
  return '';
}

interface PlaceholderContext {
  officeTitle: string;
  countryTitle: string;
  statusTitle: string;
  rankTitle: string;
  levelTitle: string;
  contractTypeName: string;
  country2Title: string;
}

// Exported for unit tests — the component calls this directly in its reactive useEffect.
export function applyPlaceholders(template: string, values: AdvisorValues, ctx: PlaceholderContext): string {
  const map: Record<string, string> = {
    VORNAME: values.name || '',
    NACHNAME: values.surname || '',
    BERATERNUMMER: String(values.agent_id || ''),
    FIRMA: ctx.officeTitle,
    LAND: ctx.countryTitle,
    STRASSE: values.address || '',
    PLZ: values.zip || '',
    ORT: values.city || '',
    GESCHAEFTSEMAIL: values.email_corp || '',
    PRIVATEEMAIL: values.email_private || '',
    TELEFON: values.phone || '',
    MOBILTELEFON: values.mobile || '',
    FAX: values.fax || '',
    GEBURTSTAG: formatBirthday(values.birthday),
    GESCHLECHT: formatGender(values.is_male),
    STATUS: ctx.statusTitle,
    RANG: ctx.rankTitle,
    LEVEL: ctx.levelTitle,
    VERTRAGSART: ctx.contractTypeName,
    IBAN: values.iban || '',
    BANKNAME: values.bank_name || '',
    BIC: values.bic || '',
    STEUERNUMMER: values.tax_nr || '',
    STARTDATUM: values.start_date || '',
    MYJACK_LOGIN: values.myjack_login || '',
    KREDITORENNUMMER: String(values.agent_account_nr || ''),
    RA_STRASSE: values.address2 || '',
    RA_PLZ: values.zip2 || '',
    RA_ORT: values.city2 || '',
    RA_LAND: ctx.country2Title,
    MYJACK_UNIT: '',
    TEAMLEITER: '',
    MANDATREFERENZ: '',
    MANDATSREFERENZ: values.mandateReference ?? '',
  };
  return template.replace(/\{\{(\w+)\}\}/g, (match, varName: string) =>
    map[varName] !== undefined ? map[varName] : match,
  );
}

// Exported for unit tests — wraps the re-derivation guard used by the reactive useEffect.
// CONTRACT: Returns null when rawBodyTemplate=null (effect no-op; emailContent not touched).
// CONTRACT: Returns null when wasEdited=true (suppressed to preserve staff manual edits).
// CONTRACT: Returns applyPlaceholders(rawBodyTemplate, values, ctx) otherwise.
export function deriveBodyIfReady(
  rawBodyTemplate: string | null,
  wasEdited: boolean,
  values: AdvisorValues,
  ctx: PlaceholderContext,
): string | null {
  if (rawBodyTemplate === null || wasEdited) return null;
  return applyPlaceholders(rawBodyTemplate, values, ctx);
}

// Exported for unit tests — computes submit-readiness from the three guard conditions.
// CONTRACT: Returns true when rawBodyTemplate=null (no-template path; other guards like
// CONTRACT: hasSelection still apply).
// CONTRACT: Returns true when wasEdited=true (emailContent is user-authored; always ready).
// CONTRACT: Returns true when bodyDerived=true (reactive useEffect completed derivation;
// CONTRACT: content may legitimately be '' when all placeholders resolve to empty values,
// CONTRACT: e.g. {{FAX}} for an advisor without a fax — that is a valid completed state,
// CONTRACT: not a missing-derivation signal; checking emailContent!=='' here would block
// CONTRACT: submit indefinitely for such templates).
// CONTRACT: Returns false when rawBodyTemplate is non-null, wasEdited=false, bodyDerived=false
// CONTRACT: — derivation is still pending (one-render race window after template query completes).
export function isEmailBodyReady(rawBodyTemplate: string | null, wasEdited: boolean, bodyDerived: boolean): boolean {
  return rawBodyTemplate === null || wasEdited || bodyDerived;
}

function SystemActionDialog({
  open,
  onClose,
  onComplete,
  advisorId,
  advisorValues,
  countries,
  branchOffices,
  formDirty,
  statuses = [],
  ranks = [],
  levels = [],
  contractTypes = [],
}: SystemActionDialogProps) {
  const theme = useTheme();

  const [selectedProviderId, setSelectedProviderId] = useState<number | ''>('');
  const [timing, setTiming] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledDateError, setScheduledDateError] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [editText, setEditText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [wasEdited, setWasEdited] = useState(false);
  // Mirror `wasEdited` into a ref so the `loadTemplate` onCompleted handler (which
  // runs after an async round-trip and would otherwise see a stale closure value)
  // always reads the current edit state. Used to preserve manual edits across a
  // formDirty refresh — see the onCompleted comment below.
  const wasEditedRef = useRef(false);
  useEffect(() => {
    wasEditedRef.current = wasEdited;
  }, [wasEdited]);
  const [successMessage, setSuccessMessage] = useState('');
  const [successIsScheduled, setSuccessIsScheduled] = useState(false);
  // `Von:`/`Betreff:` come from the backend (same getMailFrom/buildEmailSubject
  // the send paths use) so the preview can never diverge from the mail that is
  // actually sent. Filled from the template query (depends only on advisor +
  // action_type, both constant within this dialog).
  const [mailFrom, setMailFrom] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  // Raw (unreplaced) body template stored separately so lookup-backed placeholders
  // (STATUS, RANG, LEVEL, VERTRAGSART, RA_LAND) can be re-derived reactively if the
  // lookup arrays arrive after the template query completes (cold-page-load race).
  const [rawBodyTemplate, setRawBodyTemplate] = useState<string | null>(null);
  // Tracks whether the reactive re-derivation useEffect has completed at least one
  // derivation cycle for the current rawBodyTemplate. Reset to false on every template
  // transition (provider change, dialog open, source=none, new template load) so the
  // one-render race window (rawBodyTemplate set but effect not yet fired) correctly
  // blocks submit. Set to true by the effect after setEmailContent fires — even when
  // the derived body is '' (all placeholders empty, e.g. {{FAX}} without fax entry).
  const [bodyDerived, setBodyDerived] = useState(false);
  // Mirror rawBodyTemplate into a ref so the async onCompleted handler can compare
  // the incoming template against the currently committed value without a stale closure.
  // Used to skip setBodyDerived(false) when the same template string comes back
  // (same-template reload via formDirty refresh) — see the CONTRACT guard in onCompleted.
  const rawBodyTemplateRef = useRef<string | null>(null);
  useEffect(() => {
    rawBodyTemplateRef.current = rawBodyTemplate;
  }, [rawBodyTemplate]);

  const country = countries.find((c) => c.id === advisorValues.country_id) ?? advisorValues.country;
  const office = branchOffices.find((o) => o.id === advisorValues.branch_office_id) ?? advisorValues.branch_office;
  const currentCountryTitle = country?.title || '';
  const currentOfficeTitle = office?.title || '';
  const currentStatusTitle = statuses.find((s) => s.id === advisorValues.status_id)?.title || '';
  const currentRankTitle = ranks.find((r) => r.id === advisorValues.rank_id)?.title || '';
  const currentLevelTitle = levels.find((l) => l.id === advisorValues.level_id)?.title || '';
  const currentContractTypeName = contractTypes.find((c) => c.id === advisorValues.contract_type_id)?.title || '';
  const currentCountry2Title = countries.find((c) => c.id === advisorValues.country2_id)?.title || '';

  const { data: systemsData, loading: systemsLoading } = useQuery(getAdvisorSystems, {
    variables: { advisor_id: advisorId },
    skip: !open || !advisorId || advisorId === 0,
    fetchPolicy: 'network-only',
  });

  const providers: AdvisorSystem[] = systemsData?.getAdvisorSystems || [];
  const selectedProvider = providers.find((p) => p.provider_id === selectedProviderId) || null;
  const hasSelection = Boolean(selectedProvider);

  // Earliest selectable day = tomorrow in EUROPE/BERLIN, NOT the admin's browser-local calendar
  // day. `scheduled_at` is anchored at 00:00 Europe/Berlin and the backend rejects a date that is
  // not strictly in the future (INVALID_SCHEDULED_DATE). Deriving "tomorrow" from the browser
  // timezone would let a viewer west/east of Berlin near midnight pick a day the backend rejects
  // — or block a still-valid Berlin day. Shared with the edit dialog via getTomorrowDateBerlin
  // (features/system-accesses/utils/format.ts) so both scheduled-mail entry points use one
  // Berlin-anchored lower bound (Audit/Review P2; HOTSPOTS "Mindestdatum … Tag-Shift" class).
  const validateScheduledDate = (dateStr: string): string => {
    if (!dateStr) return '';
    if (dateStr < getTomorrowDateBerlin()) return 'Termin darf nur in der Zukunft liegen.';
    return '';
  };

  const [loadTemplate, { loading: templateLoading }] = useLazyQuery(getSystemActionTemplate, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getSystemActionTemplate) {
        const { body_template: bodyTemplate, source, subject, mail_from: mailFromValue } = data.getSystemActionTemplate;
        setMailSubject(subject || '');
        setMailFrom(mailFromValue || '');
        // Preserve manual edits across a formDirty refresh: this effect re-runs on
        // the dirty→clean transition to refresh the backend-derived subject/from,
        // but once staff has customized the mail body in this dialog, overwriting
        // emailContent/editText/isEditing here would silently discard those edits and
        // send the regenerated template instead (RITA-1307 Review P1). Only refresh
        // the header in that case and keep the user's body. A provider change resets
        // wasEdited (and the ref) to false first, so a freshly selected provider
        // still loads its template normally.
        if (wasEditedRef.current) {
          return;
        }
        if (source === 'none') {
          setBodyDerived(false);
          setRawBodyTemplate(null);
          setIsEditing(true);
          // Explicit reset (source=none: no backend template for this provider).
          // rawBodyTemplate=null makes the re-derivation effect a no-op (CONTRACT above),
          // so we clear emailContent here to reset any previously loaded template body.
          setEmailContent('');
          setEditText('');
        } else {
          // Store raw template; emailContent is computed reactively by the useEffect
          // below so lookup-backed placeholders pick up their values even when the
          // lookup arrays (statuses, ranks, etc.) arrive after this callback fires.
          // Reset bodyDerived so the race-window guard (emailBodyReady) correctly
          // blocks submit until the reactive effect fires for this new template.
          // CONTRACT: bodyDerived is cleared ONLY when the incoming bodyTemplate differs
          // CONTRACT: from the last committed rawBodyTemplate (rawBodyTemplateRef.current).
          // CONTRACT: Same-template reload (formDirty refresh returning the same template
          // CONTRACT: string): rawBodyTemplate state unchanged → re-derivation effect
          // CONTRACT: would not rerun → must NOT clear bodyDerived or submit is permanently
          // CONTRACT: locked. (P1, RITA-1436)
          if (bodyTemplate !== rawBodyTemplateRef.current) {
            setBodyDerived(false);
          }
          setRawBodyTemplate(bodyTemplate);
          setIsEditing(false);
          setEditText('');
        }
      }
    },
  });

  // Re-derive emailContent whenever rawBodyTemplate or any lookup-backed title changes.
  // This covers the cold-page-load race where STATUS/RANG/LEVEL/VERTRAGSART/RA_LAND
  // arrive AFTER the template query completes, which would otherwise freeze empty strings
  // into the body materialized by onCompleted.
  // CONTRACT: emailContent is re-derived from rawBodyTemplate whenever rawBodyTemplate or
  // CONTRACT: any lookup-title (currentStatusTitle, currentRankTitle, currentLevelTitle,
  // CONTRACT: currentContractTypeName, currentCountry2Title, currentOfficeTitle,
  // CONTRACT: currentCountryTitle) or advisorValues changes.
  // CONTRACT: Re-derivation is suppressed while wasEdited=true to preserve staff manual edits.
  // CONTRACT: rawBodyTemplate=null → this effect is a no-op (setEmailContent not called here).
  // CONTRACT: State-reset callers (dialog open, provider change, source=none onCompleted) clear
  // CONTRACT: emailContent via their own explicit setState — those resets are intentional and
  // CONTRACT: scoped to their reset paths, not governed by this effect's no-op promise.
  // CONTRACT: onCompleted clears bodyDerived ONLY on an actual template transition
  // CONTRACT: (rawBodyTemplateRef.current !== incoming template) — same-template reload
  // CONTRACT: must never clear bodyDerived because rawBodyTemplate dep would not change
  // CONTRACT: and the effect would not rerun to restore it.
  useEffect(() => {
    const body = deriveBodyIfReady(rawBodyTemplate, wasEdited, advisorValues, {
      officeTitle: currentOfficeTitle,
      countryTitle: currentCountryTitle,
      statusTitle: currentStatusTitle,
      rankTitle: currentRankTitle,
      levelTitle: currentLevelTitle,
      contractTypeName: currentContractTypeName,
      country2Title: currentCountry2Title,
    });
    if (body !== null) {
      setEmailContent(body);
      // Signal that derivation completed for the current rawBodyTemplate — even when body
      // is '' (all placeholders resolved to empty). isEmailBodyReady checks bodyDerived,
      // not emailContent, so a legitimately empty body still enables submit after this fires.
      setBodyDerived(true);
    }
  }, [
    rawBodyTemplate,
    wasEdited,
    advisorValues,
    currentOfficeTitle,
    currentCountryTitle,
    currentStatusTitle,
    currentRankTitle,
    currentLevelTitle,
    currentContractTypeName,
    currentCountry2Title,
  ]);

  const [executeAction, { loading: executing }] = useMutation(executeSystemActionMutation);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedProviderId('');
      setTiming('now');
      setScheduledDate('');
      setScheduledDateError('');
      setSuccessMessage('');
      // Explicit full reset: clears emailContent independently of rawBodyTemplate.
      // (rawBodyTemplate=null below makes the re-derivation effect a no-op; we clear here
      //  to ensure no stale body from a previous session is shown on re-open.)
      setEmailContent('');
      setEditText('');
      setIsEditing(false);
      setWasEdited(false);
      setMailFrom('');
      setMailSubject('');
      setBodyDerived(false);
      setRawBodyTemplate(null);
    }
  }, [open]);

  // Load template when a provider is selected.
  //
  // `formDirty` is a dependency on purpose: the body (`emailContent`) is derived
  // from the live advisor form values at fetch time and the subject is built
  // backend-side from the PERSISTED advisor record. If the user saves the advisor
  // in place while this dialog stays open, `advisorId` does not change — so without
  // `formDirty` in the deps this effect would not rerun and the dialog would keep
  // the pre-save preview/subject while sending is re-enabled, delivering a stale,
  // customer-facing mail (RITA-1307 Review P1). Re-running on the dirty→clean
  // transition refetches the subject and re-derives the body from the now-saved
  // values, keeping preview and send payload consistent with the persisted advisor.
  useEffect(() => {
    if (open && selectedProviderId !== '') {
      loadTemplate({
        variables: {
          provider_id: selectedProviderId,
          action_type: ACTION_TYPE,
          advisor_id: advisorId,
        },
      });
    }
  }, [open, selectedProviderId, advisorId, formDirty, loadTemplate]);

  const handleProviderChange = (event: SelectChangeEvent<number | ''>) => {
    const raw = event.target.value;
    const next = raw === '' ? '' : Number(raw);
    setSelectedProviderId(next);
    // Explicit reset on provider switch: clear emailContent to avoid showing the previous
    // provider's template while the new one loads. rawBodyTemplate=null (set below) makes
    // the re-derivation effect a no-op until the next template query completes.
    setEmailContent('');
    setEditText('');
    setIsEditing(false);
    setWasEdited(false);
    // Clear the previous provider's header metadata immediately. `mailFrom`/
    // `mailSubject` are only refilled when the next getSystemActionTemplate query
    // succeeds; if that fetch fails (provider/advisor deleted, network error) the
    // dialog would otherwise keep showing provider B while still rendering provider
    // A's `Von:`/`Betreff:` header — a misleading mismatch that persists until the
    // dialog is closed (RITA-1307 Review P2). Resetting here keeps the header in
    // sync with the just-cleared body.
    setMailFrom('');
    setMailSubject('');
    setBodyDerived(false);
    setRawBodyTemplate(null);
  };

  const handleSubmit = async () => {
    if (!selectedProvider) return;
    // If still in edit mode, convert plain text to HTML before sending
    const finalContent = isEditing ? plainTextToHtml(editText) : emailContent;
    try {
      await executeAction({
        variables: {
          advisor_id: advisorId,
          input: {
            provider_id: selectedProvider.provider_id,
            action_type: ACTION_TYPE,
            scheduled_at: timing === 'scheduled' ? `${scheduledDate}T00:00:00` : null,
            mail_content: finalContent,
            is_manual_edit: wasEdited,
          },
        },
      });
      if (timing === 'scheduled') {
        const formattedDate = scheduledDate.split('-').reverse().join('.');
        setSuccessMessage(`E-Mail terminiert f\u00fcr ${formattedDate}`);
        setSuccessIsScheduled(true);
      } else {
        setSuccessMessage('E-Mail gesendet!');
        setSuccessIsScheduled(false);
      }
      setTimeout(() => {
        onComplete();
      }, 1200);
    } catch {
      // Error is handled by Apollo error link
    }
  };

  const handleEditToggle = () => {
    if (!isEditing) {
      // Entering edit mode: convert HTML to plain text
      setEditText(htmlToPlainText(emailContent));
      setWasEdited(true);
    } else {
      // Leaving edit mode: convert plain text back to HTML
      setEmailContent(plainTextToHtml(editText));
    }
    setIsEditing(!isEditing);
  };

  const advisorFields = [
    { label: 'VORNAME', value: advisorValues.name },
    { label: 'NACHNAME', value: advisorValues.surname },
    { label: 'BERATER-NR.', value: String(advisorValues.agent_id) },
    { label: 'FIRMA', value: currentOfficeTitle || '\u2014' },
    { label: 'ADRESSE', value: advisorValues.address || '\u2014' },
    { label: 'PLZ / ORT', value: `${advisorValues.zip || ''} ${advisorValues.city || ''}`.trim() || '\u2014' },
    { label: 'LAND', value: currentCountryTitle || '\u2014' },
    { label: 'E-MAIL', value: advisorValues.email_corp || advisorValues.email_private || '\u2014' },
  ];

  // Email content can be manually edited by staff before sending (RITA-1307),
  // so it is no longer guaranteed to be admin-authored template HTML. Sanitize
  // with DOMPurify before rendering to avoid stored-XSS.
  const renderEmailHtml = (html: string) => ({
    __html: sanitizeEmailHtml(html) || '<em>Keine Vorlage verf\u00fcgbar</em>',
  });

  // Block submit while rawBodyTemplate is set but the reactive useEffect has not yet derived
  // emailContent. The effect runs after the render that flips templateLoading to false, so
  // there is a one-render window where submit is re-enabled but bodyDerived is still false.
  // See isEmailBodyReady (exported above) for the full CONTRACT and testable form.
  const emailBodyReady = isEmailBodyReady(rawBodyTemplate, wasEdited, bodyDerived);

  const submitDisabled =
    !hasSelection ||
    executing ||
    templateLoading ||
    !emailBodyReady ||
    formDirty ||
    (timing === 'scheduled' && (!scheduledDate || !!scheduledDateError));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: APPLY_STYLE.bgAlpha,
          borderBottom: `1px solid ${APPLY_STYLE.borderAlpha}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: APPLY_STYLE.color, display: 'flex' }}>
            <CheckCircleOutlineIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: APPLY_STYLE.color }}>
              {APPLY_STYLE.label}
              {selectedProvider ? `: ${selectedProvider.provider_name}` : ''}
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
              {advisorValues.name} {advisorValues.surname} &middot; Nr. {advisorValues.agent_id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {successMessage ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {successIsScheduled ? (
              <CalendarTodayIcon sx={{ fontSize: 48, color: '#58A6FF', mb: 1 }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#4ADE80', mb: 1 }} />
            )}
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: successIsScheduled ? '#58A6FF' : '#4ADE80' }}>
              {successMessage}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Provider Selection */}
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: theme.palette.text.disabled,
                mb: 1.5,
              }}
            >
              DIENSTLEISTER AUSW&Auml;HLEN
            </Typography>
            {systemsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel id="provider-select-label">Dienstleister</InputLabel>
                <Select
                  labelId="provider-select-label"
                  id="provider-select"
                  value={selectedProviderId}
                  label="Dienstleister"
                  onChange={handleProviderChange}
                  inputProps={{ 'data-testid': 'provider-select' }}
                  renderValue={(value) => {
                    if (!value) return '';
                    const p = providers.find((prov) => prov.provider_id === Number(value));
                    return p ? p.provider_name : '';
                  }}
                >
                  {providers.length === 0 && (
                    <MenuItem disabled value="">
                      Keine aktiven Dienstleister vorhanden
                    </MenuItem>
                  )}
                  {providers.map((p) => {
                    const isActive = p.access_status === 'active';
                    // `pending` = ein Versand für dieses System läuft gerade
                    // (claim-before-send / Recovery-Fenster). Auswahl sperren,
                    // sonst löst ein zweites executeSystemAction eine Doppel-Mail
                    // für denselben Zugang aus (RITA-1307 Review P1).
                    const isPending = p.access_status === 'pending';
                    const isBlocked = isActive || isPending;
                    return (
                      <MenuItem
                        key={p.provider_id}
                        value={p.provider_id}
                        disabled={isBlocked}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}
                      >
                        <Typography sx={{ fontSize: 14 }}>{p.provider_name}</Typography>
                        {isBlocked && (
                          <Chip
                            size="small"
                            label={isActive ? 'bereits aktiv' : 'wird versendet'}
                            sx={{
                              ml: 'auto',
                              bgcolor: alpha('#9CA3AF', 0.18),
                              color: theme.palette.text.disabled,
                              fontSize: 11,
                              fontWeight: 600,
                              height: 22,
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}

            {!hasSelection && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: theme.palette.text.disabled,
                  textAlign: 'center',
                  py: 2,
                  mb: 2,
                }}
              >
                Bitte w&auml;hle zuerst einen Dienstleister aus, um Beraterdaten, E-Mail-Vorschau und
                Ausf&uuml;hrungszeitpunkt zu sehen.
              </Typography>
            )}

            {hasSelection && (
              <>
                {/* Advisor Data */}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    color: theme.palette.text.disabled,
                    mb: 1.5,
                  }}
                >
                  BERATERDATEN &ndash; AUTOMATISCH BEF&Uuml;LLT
                </Typography>
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.grey[500], 0.06),
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    p: 2,
                    mb: 3,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                  }}
                >
                  {advisorFields.map((field) => (
                    <Box key={field.label}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          color: theme.palette.text.disabled,
                          mb: 0.25,
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{field.value}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Timing Toggle */}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    color: theme.palette.text.disabled,
                    mb: 1,
                  }}
                >
                  AUSF&Uuml;HRUNGSZEITPUNKT
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: timing === 'scheduled' ? 1.5 : 3 }}>
                  <Button
                    fullWidth
                    startIcon={<BoltIcon />}
                    onClick={() => setTiming('now')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.25,
                      borderRadius: 1.5,
                      ...(timing === 'now'
                        ? {
                            bgcolor: alpha(APPLY_STYLE.color, 0.1),
                            color: APPLY_STYLE.color,
                            border: `1px solid ${alpha(APPLY_STYLE.color, 0.4)}`,
                          }
                        : {
                            bgcolor: 'transparent',
                            color: theme.palette.text.secondary,
                            border: `1px solid ${theme.palette.divider}`,
                          }),
                    }}
                  >
                    Sofort
                  </Button>
                  <Button
                    fullWidth
                    startIcon={<CalendarTodayIcon />}
                    onClick={() => setTiming('scheduled')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.25,
                      borderRadius: 1.5,
                      ...(timing === 'scheduled'
                        ? {
                            bgcolor: alpha(APPLY_STYLE.color, 0.1),
                            color: APPLY_STYLE.color,
                            border: `1px solid ${alpha(APPLY_STYLE.color, 0.4)}`,
                          }
                        : {
                            bgcolor: 'transparent',
                            color: theme.palette.text.secondary,
                            border: `1px solid ${theme.palette.divider}`,
                          }),
                    }}
                  >
                    Terminieren
                  </Button>
                </Box>
                {timing === 'scheduled' && (
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={scheduledDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      setScheduledDate(val);
                      setScheduledDateError(validateScheduledDate(val));
                    }}
                    error={!!scheduledDateError}
                    helperText={scheduledDateError || 'Versand erfolgt am gewählten Tag um 00:00 Uhr (Europe/Berlin).'}
                    inputProps={{ min: getTomorrowDateBerlin() }}
                    sx={{ mb: 3 }}
                  />
                )}

                {/* Email Preview */}
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.grey[500], 0.06),
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        color: theme.palette.text.disabled,
                      }}
                    >
                      E-MAIL VORSCHAU
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={handleEditToggle}
                      sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        ...(isEditing
                          ? {
                              bgcolor: alpha('#4ADE80', 0.15),
                              color: '#4ADE80',
                              border: `1px solid ${alpha('#4ADE80', 0.3)}`,
                            }
                          : {
                              bgcolor: alpha('#58A6FF', 0.1),
                              color: '#58A6FF',
                              border: `1px solid ${alpha('#58A6FF', 0.3)}`,
                            }),
                      }}
                    >
                      {isEditing ? 'Aktiv' : 'Bearbeiten'}
                    </Button>
                  </Box>

                  <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled, mb: 0.25 }}>
                    <strong>An:</strong> {selectedProvider?.recipient_email}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled, mb: 0.25 }}>
                    <strong>Von:</strong> {mailFrom || '—'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled, mb: 1.5 }}>
                    <strong>Betreff:</strong> {mailSubject || '—'}
                  </Typography>

                  {templateLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  {!templateLoading && isEditing && (
                    <TextField
                      multiline
                      fullWidth
                      minRows={4}
                      maxRows={12}
                      value={editText}
                      onChange={(e) => {
                        setEditText(e.target.value);
                        setWasEdited(true);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: 13,
                        },
                      }}
                    />
                  )}
                  {!templateLoading && !isEditing && (
                    <Box
                      sx={{
                        fontSize: 13,
                        color: theme.palette.text.primary,
                        maxHeight: 120,
                        overflow: 'auto',
                        lineHeight: 1.6,
                      }}
                      // eslint-disable-next-line react/no-danger -- Content from admin-created letter templates (Modul 1), same pattern as LetterTemplateForm.tsx
                      dangerouslySetInnerHTML={renderEmailHtml(emailContent)}
                    />
                  )}
                </Box>

                {wasEdited && (
                  <Alert
                    severity="warning"
                    sx={{
                      mb: 2,
                      bgcolor: alpha('#F59E0B', 0.08),
                      border: `1px solid ${alpha('#F59E0B', 0.3)}`,
                      '& .MuiAlert-icon': { color: '#F59E0B' },
                      fontSize: 13,
                    }}
                  >
                    E-Mail wurde manuell bearbeitet &ndash; wird im Protokoll vermerkt.
                  </Alert>
                )}
              </>
            )}

            {/* Unsaved-advisor guard: while the form is dirty the subject (from
                the saved record) and the body (from the live form) can describe
                different advisors — block the send until the user saves. */}
            {formDirty && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  bgcolor: alpha('#F59E0B', 0.08),
                  border: `1px solid ${alpha('#F59E0B', 0.3)}`,
                  '& .MuiAlert-icon': { color: '#F59E0B' },
                  fontSize: 13,
                }}
              >
                Die Beraterdaten wurden ge&auml;ndert, aber noch nicht gespeichert. Betreff (aus den gespeicherten
                Daten) und Inhalt (aus dem Formular) k&ouml;nnten unterschiedliche Angaben enthalten. Bitte speichere
                den Berater zuerst, dann kann die E-Mail gesendet werden.
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                onClick={onClose}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.25,
                  borderRadius: 1.5,
                  color: theme.palette.text.secondary,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitDisabled}
                sx={{
                  flex: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.25,
                  borderRadius: 1.5,
                  bgcolor: APPLY_STYLE.color,
                  color: '#000',
                  '&:hover': {
                    bgcolor: alpha(APPLY_STYLE.color, 0.85),
                  },
                  '&.Mui-disabled': {
                    bgcolor: alpha(APPLY_STYLE.color, 0.3),
                    color: alpha('#000', 0.5),
                  },
                }}
                startIcon={executing ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
              >
                {timing === 'scheduled'
                  ? `Terminieren f\u00fcr ${scheduledDate || '...'}`
                  : `${APPLY_STYLE.label} & E-Mail senden`}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SystemActionDialog;
